import React from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
} from "recharts";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { RiUserSettingsLine } from "react-icons/ri";
import { BsCalendarWeek } from "react-icons/bs";

import { getSalesOverview } from "../../api/tickets";
import { COLORS } from "../../utils/colors";
import { useState } from "react";
import { useEffect } from "react";
import { Onboarding } from "./Onboarding";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButtonGroup-grouped": {
    margin: theme.spacing(0.5),
    border: 0,
    "&.Mui-disabled": {
      border: 0,
    },
    "&:not(:first-of-type)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-of-type": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}));

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label" style={{ color: COLORS.grey }}>
          Tickets sold
        </p>
        <p className="label">{label}</p>
        <p
          className="label"
          style={{ fontSize: "34px" }}
        >{`${payload[0].value}`}</p>
      </div>
    );
  }

  return null;
};

export const Overview = ({ userData, supabase, handleSnackbar }) => {
  const [ticketSold, setTicketSold] = useState([]);
  const [numberOfTickets, setNumberOfTickets] = useState(0);
  const [dayPeriod, setDayPeriod] = useState(30);
  const [doOnboarding, setDoOnboarding] = useState(
    !userData || !userData.operators?.length > 0
  );

  function createArrayOfObjects(originalArray, newDayPeriod) {
    const result = [];
    const currentDate = new Date();

    if (newDayPeriod == 0) {
      return originalArray;
    } else {
      for (let i = newDayPeriod - 1; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - i);
        const formattedDate = date.toISOString().split("T")[0];

        const originalObject = originalArray.find(
          (obj) => obj._id === formattedDate
        );
        const tickets_sold = originalObject
          ? originalObject.tickets_sold
          : null;

        result.push({ _id: formattedDate, tickets_sold });
      }

      return result;
    }
  }

  const changeDayPeriod = async (event, newDayPeriod) => {
    if (newDayPeriod !== null) {
      setDayPeriod(newDayPeriod);

      try {
        const { data, error } = await getSalesOverview({
          dayPeriod: newDayPeriod,
          apiKey: userData.operators[0].api_key,
        });

        if (error) {
          if (!error.message.includes("No ticket found")) {
            handleSnackbar(error.message, "error");
          }
        }

        if (data) {
          data.sort((a, b) => a._id.localeCompare(b._id));
          setNumberOfTickets(data.reduce((a, b) => a + b.tickets_sold, 0));

          const fullRangeData = createArrayOfObjects(data, newDayPeriod);
          setTicketSold(fullRangeData);
        }
      } catch (err) {
        console.log("Error: ", err);
        handleSnackbar("Error while fetching data [101]", "error");
      }
    }
  };

  useEffect(async () => {
    if (userData && userData.operators?.length > 0) {
      setDoOnboarding(false);
    }

    try {
      if (userData && userData.operators?.length > 0) {
        const { data, error } = await getSalesOverview({
          dayPeriod,
          apiKey: userData.operators[0].api_key,
        });

        if (error) {
          if (!error.message.includes("No ticket found")) {
            handleSnackbar(error.message, "error");
          }
        }

        if (data) {
          data.sort((a, b) => a._id.localeCompare(b._id));
          setNumberOfTickets(data.reduce((a, b) => a + b.tickets_sold, 0));

          const fullRangeData = createArrayOfObjects(data, dayPeriod);
          setTicketSold(fullRangeData);
        }
      }
    } catch (err) {
      console.log("Error: ", err);
      handleSnackbar("Error while fetching data [102]", "error");
    }
  }, [userData]);

  return (
    <div className="overview-container">
      {doOnboarding ? (
        <Onboarding
          userData={userData}
          supabase={supabase}
          handleSnackbar={handleSnackbar}
        />
      ) : (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "25px",
              marginBottom: "25px",
            }}
          >
            <div className="small-container">
              <div
                className="container-row"
                style={{
                  width: "-webkit-fill-container",
                  justifyContent: "space-between",
                }}
              >
                <p>Operators</p>
                <RiUserSettingsLine color={COLORS.mainAccent} />
              </div>
              <h2 style={{ fontSize: "34px" }}>
                {userData?.operators?.length}
              </h2>
            </div>
            <div className="small-container">
              <div
                className="container-row"
                style={{
                  width: "-webkit-fill-container",
                  justifyContent: "space-between",
                }}
              >
                <p>Events created</p>
                <BsCalendarWeek color={COLORS.mainAccent} />
              </div>
              <h2 style={{ fontSize: "34px" }}>
                {userData?.events_list?.length}
              </h2>
            </div>
          </div>
          <div className="graph-container">
            <div className="graph-header">
              <div>
                <p style={{ color: COLORS.grey }}>Total tickets sold</p>
                <p style={{ fontSize: "34px" }}>{numberOfTickets}</p>
              </div>
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  flexWrap: "wrap",
                }}
              >
                <StyledToggleButtonGroup
                  size="small"
                  value={dayPeriod}
                  exclusive
                  onChange={changeDayPeriod}
                >
                  <ToggleButton value="1" selected={dayPeriod == 1}>
                    <p style={{ fontSize: "14px", margin: "0px 20px" }}>1D</p>
                  </ToggleButton>
                  <ToggleButton value="7" selected={dayPeriod == 7}>
                    <p style={{ fontSize: "14px", margin: "0px 20px" }}>7D</p>
                  </ToggleButton>
                  <ToggleButton value="15" selected={dayPeriod == 15}>
                    <p style={{ fontSize: "14px", margin: "0px 20px" }}>15D</p>
                  </ToggleButton>
                  <ToggleButton value="30" selected={dayPeriod == 30}>
                    <p style={{ fontSize: "14px", margin: "0px 20px" }}>30D</p>
                  </ToggleButton>
                  <ToggleButton value="0" selected={dayPeriod == 0}>
                    <p style={{ fontSize: "14px", margin: "0px 20px" }}>
                      TOTAL
                    </p>
                  </ToggleButton>
                </StyledToggleButtonGroup>
              </Paper>
            </div>
            <ResponsiveContainer width="100%" height="80%">
              <ComposedChart
                width={400}
                height={100}
                data={ticketSold}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20,
                }}
              >
                <XAxis
                  dataKey="_id"
                  stroke="black"
                  tick={{
                    fontFamily: "AuthenticSans90",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                  domain={[
                    new Date(Date.now() - dayPeriod * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .slice(0, 10),
                    new Date().toISOString().slice(0, 10),
                  ]}
                />
                <YAxis
                  dataKey="tickets_sold"
                  orientation="right"
                  stroke="black"
                  tick={{
                    fontFamily: "AuthenticSans90",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                  domain={ticketSold.length !== 0 ? [0, ticketSold] : [0, 1000]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="tickets_sold"
                  barSize={20}
                  fill={COLORS.mainAccent}
                  label={{
                    value: "Tickets Sold",
                    fontFamily: "AuthenticSans90",
                    fontSize: "12px",
                    fontWeight: "bold",
                    color: "#000",
                  }}
                />
                {/* <CartesianGrid stroke="#ccc" />{" "} */}
                <Line type="monotone" dataKey="value" stroke="#8884d8" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div style={{ height: "50px" }} />
    </div>
  );
};
